import { useEffect, useState, useRef } from "react";
import { Modal } from ".";
import { request } from "../../../utils";
import { ORDER_STATUS } from "../../../utils/constant";
import { useNavigate } from "react-router";
import { TimeIcon } from "../../../icons";
import { useOrdersStore } from "../../../stores/useOrderStore";
import { waitForLoadingToComplete } from "../../../utils/helpers";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
}

interface OrderData {
  id: string;
  status: ORDER_STATUS;
  amount: string;
  fromCurrency: string;
  toCurrency: string;
  buyerDebitPaymentMethod: string;
  buyerCreditPaymentMethod: string;
}

export default function AutoProcessingModal({
  isOpen,
  onClose,
  orderId,
}: Props) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"PROCESSING" | "SUCCESS" | "FAILED" | "PENDING">("PENDING");
  const [message, setMessage] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const { updateOrder } = useOrdersStore();
  
  // Refs for cleanup
  const pollingRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const elapsedTimeRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);

  // Cleanup all intervals and timeouts
  const cleanup = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    elapsedTimeRef.current = 0;
  };

  // Navigate to orders page and close modal
  const navigateToOrders = () => {
    cleanup();
    if (isMountedRef.current) {
      onClose();
      navigate("/exchanges");
    }
  };

  // Polling function with timeout
  const startPolling = () => {
    if (!isOpen || !orderId) return;

    const interval = 3000; // 2s
    const maxPollingTime = 30000; // 30s max polling time

    // ---- Set overall timeout to stop polling after 30 seconds
    timeoutRef.current = setTimeout(() => {
      cleanup();
      if (isMountedRef.current) {
        setStatus("PENDING");
        setMessage("Your payment is being processed. Please wait...");
        setError("Please check your exchange  page for the latest status.");
        
        // Navigate after showing message for a moment
        setTimeout(() => {
          navigateToOrders();
        }, 3000);
      }
    }, maxPollingTime);

    const poll = async () => {
      try {
        const response = await request.get({
          url: `/order/${orderId}`,
        });
        
        let orderData;
        let orderStatus: ORDER_STATUS | undefined;

        if (response.success && response.data) {
          orderData = response.data as OrderData;
          orderStatus = orderData.status;
        }

        // Handle different statuses
        if (orderStatus === ORDER_STATUS.SUCCESSFUL) {
          cleanup();
          
          if (isMountedRef.current) {
            setStatus("SUCCESS");
            setMessage("Your exchange has been completed successfully.");
            
            // Update store
            if (orderData) {
              updateOrder(orderId, {
                status: ORDER_STATUS.SUCCESSFUL,
                buyerMadePayment: true,
              });
            }

            await waitForLoadingToComplete();

            // Close modal after 2 seconds and navigate
            setTimeout(() => {
              navigateToOrders();
            }, 3000);
          }
          return true;
          
        } else if (orderStatus === ORDER_STATUS.PROCESSING) {
          if (isMountedRef.current) {
            setStatus("PROCESSING");
            setMessage("Your payment is being processed. Please wait...");
          }
          
        } else if (orderStatus === ORDER_STATUS.FAILED) {
          cleanup();
          
          if (isMountedRef.current) {
            setStatus("FAILED");
            setError("Payment failed. Please try again or contact support.");
            
            updateOrder(orderId, {
              status: ORDER_STATUS.FAILED,
              buyerMadePayment: false,
            });
            
            await waitForLoadingToComplete(10, 200);

            setTimeout(() => {
              navigateToOrders();
            }, 3000);
          }
          return false;
          
        } else if (orderStatus === ORDER_STATUS.PENDING) {
          elapsedTimeRef.current += interval;
          
          if (isMountedRef.current) {
            setStatus("PROCESSING");
            setMessage("Your payment is being processed. Please wait...");
          }
        }

      } catch (err) {
        console.error("Polling error", err);
        // Continue polling even if there's an error (network issue)
        if (isMountedRef.current) {
          setStatus("PROCESSING");
          setMessage("Your payment is being processed. Please wait...");
        }
      }
    };

    // Start immediate poll and then set interval
    poll();
    pollingRef.current = setInterval(poll, interval);
  };

  // ---- Handle manual close (X button)
  const handleManualClose = () => {
    cleanup();
    
    if (status === "PENDING" || status === "PROCESSING") {
      updateOrder(orderId, {
        status: ORDER_STATUS.PENDING,
        buyerMadePayment: false,
      });
    }
    
    navigateToOrders();
  };

  // ---- Start polling when modal opens
  useEffect(() => {
    if (isOpen && orderId) {
      isMountedRef.current = true;
      startPolling();
    }
    
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [isOpen, orderId]);

  useEffect(() => {
    if (isOpen) {
      setStatus("PENDING");
      setMessage("");
      setError(null);
      elapsedTimeRef.current = 0;
    }
  }, [isOpen]);

  // ---- Helper function to get status text
  const getStatusText = () => {
    switch (status) {
      case "PENDING":
        return "Your payment is being processed. Please wait...";
      case "PROCESSING":
        return message || "Your payment is being processed. Please wait...";
      case "SUCCESS":
        return message || "Your exchange has been completed successfully.";
      case "FAILED":
        return error || "We couldn't confirm the payment at the moment.";
      default:
        return "";
    }
  };

  // ---- Helper function to get status title
  const getStatusTitle = () => {
    switch (status) {
      case "PENDING":
        return "Processing Payment ⌛";
      case "PROCESSING":
        return "Processing Payment ⏳";
      case "SUCCESS":
        return "Payment Successful 🎉";
      case "FAILED":
        return "Processing Delayed";
      default:
        return "";
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleManualClose} // Use custom handler for close
      className="max-w-sm"
      disableBackdropClick={status === "PENDING" || status === "PROCESSING"} // Prevent accidental clicks outside
    >
      <div className="p-6 text-center">
        {(status === "PENDING" || status === "PROCESSING") && (
          <>
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-[#EB2E67]" />
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
              {getStatusTitle()}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {getStatusText()}
            </p>

            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <TimeIcon className="h-4 w-4" />
              <span>This may take up to 30 seconds</span>
            </div>
            
            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              {/* <button
                onClick={handleManualClose}
                className="text-[#EB2E67] hover:text-[#d12b5c] underline transition-colors"
              >
                Close and check orders
              </button> */}
            </div>
          </>
        )}

        {status === "SUCCESS" && (
          <>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
              <svg
                className="h-6 w-6 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-green-600 dark:text-green-400">
              {getStatusTitle()}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {getStatusText()}
            </p>
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              Redirecting to exchange history page...
            </p>
          </>
        )}

        {status === "FAILED" && (
          <>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <svg
                className="h-6 w-6 text-red-600 dark:text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-red-600 dark:text-red-400">
              {getStatusTitle()}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {getStatusText()}
            </p>
            <button
              onClick={handleManualClose}
              className="mt-4 px-6 py-2 text-sm bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 transition-colors"
            >
              View Exchanges
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}